/**
 * AdminHome — the Retail Operations home (/retail), redesigned 0.53.0 (Bill 2026-11-03).
 *
 *   What do you want to do?  client-side search over every page and task the person can open.
 *   Common tasks             from platforms/adminNav.ts (RETAIL_TASKS), shown when the person can open
 *                            the page the task leads to (same role gates as the menu).
 *   Recent changes           the latest five rows of the configuration change history
 *                            (GET /v3/admin/config-changes, dbo.ConfigChangeLog), with a link to it.
 *   Needs attention          not shown: Retail has no admin status summary to read today, and the
 *                            home never makes one up.
 */

import { useEffect, useMemo, useState } from 'react';
import { PlatformHome, type RecentChange } from '@retail-ops/panel-shell';
import type { ConfigChangeRow } from '@retail-ops/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { retailNav, retailTasksFor, useAdminShell } from '@/platforms/adminNav';
import { RETAIL_BASE } from '@/platforms/retail/retailPages';
import { SectionCards, ShellLink, useSearchPick, whenText } from '@/platforms/homeShared';

const OP_LABEL: Record<string, string> = { Insert: 'Added', Update: 'Changed', Delete: 'Deleted', SeedSql: 'SQL script run' };

function keyText(row: ConfigChangeRow): string {
  if (!row.keyJson) return '';
  try {
    const k = JSON.parse(row.keyJson) as Record<string, unknown>;
    return Object.values(k)
      .map((v) => (v === null || v === undefined ? '' : typeof v === 'string' ? v : JSON.stringify(v)))
      .filter(Boolean)
      .join(' · ');
  } catch {
    return row.keyJson;
  }
}

function toRecent(row: ConfigChangeRow): RecentChange {
  const what = row.entityType ?? row.tableName;
  const key = keyText(row);
  return {
    id: String(row.changeId),
    what: `${OP_LABEL[row.operation] ?? row.operation}: ${what}${key ? ` ${key}` : ''}`,
    why: row.reason,
    who: row.actor,
    when: whenText(row.changedAt),
  };
}

export function AdminHome(): JSX.Element {
  const { user } = useAuth();
  const client = useApiClient();
  const roles = useMemo(() => user?.roles ?? [], [user]);
  const { searchItems } = useAdminShell(RETAIL_BASE);
  const onPick = useSearchPick();
  const tasks = useMemo(() => retailTasksFor(roles), [roles]);
  const nav = useMemo(() => retailNav(roles), [roles]);
  const canSeeHistory = nav.sections.some((s) => s.pages.some((p) => p.path === `${RETAIL_BASE}/history`));

  const [recent, setRecent] = useState<{ loading: boolean; error: string | null; items: RecentChange[] }>({ loading: true, error: null, items: [] });
  useEffect(() => {
    if (!canSeeHistory) return undefined;
    let cancelled = false;
    void client.retail.configChanges
      .list({ take: 5 })
      .then((r) => {
        if (!cancelled) setRecent({ loading: false, error: null, items: r.data.map(toRecent) });
      })
      .catch((e: unknown) => {
        if (!cancelled) setRecent({ loading: false, error: e instanceof Error ? e.message : String(e), items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [client, canSeeHistory]);

  return (
    <PlatformHome
      title="Retail Operations administration"
      intro={`Welcome, ${user?.displayName || user?.upn || ''}. Pick a task, or search for what you want to do. Every change asks for a reason and is recorded in the change history.`}
      Link={ShellLink}
      searchItems={searchItems}
      onSearchPick={onPick}
      searchPlaceholder={'What do you want to do?  e.g. "change a deposit rate", "add a holiday"'}
      tasks={tasks}
      tasksTour="admin-tiles"
      recent={canSeeHistory ? { ...recent, seeAllPath: `${RETAIL_BASE}/history`, seeAllLabel: 'See the full change history' } : null}
      footer={<SectionCards nav={nav} />}
    />
  );
}
